import { apiSlice } from "./apiSlice";

interface LocationResponse<T> {
  responseCode: number;
  data: T;
  message: string;
  totalRecords: number;
}

interface Country {
  name: string;
  code: string;
  _id: string;
}

interface State {
  name: string;
  code: string;
  country: Country;
  _id: string;
}

interface city {
  name: string;
  _id: string;
  state: State;
  country: Country;
}

const locationApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAllCountries: builder.query<
      LocationResponse<{
        countries: Array<Country>;
      }>,
      { page: number; limit: number }
    >({
      query: (params: { page: number; limit: number }) => ({
        url: `/location/countries`,
        method: "GET",
        params,
      }),

      // Provide tags for cache management
      providesTags: ["CountriesList"],

      // Cache configuration for optimal performance
      extraOptions: {
        maxRetries: 2,
        keepUnusedDataFor: 3600, // Keep in cache for 1 hour
        refetchOnMountOrArgChange: false,
        refetchOnFocus: false,
        refetchOnReconnect: false,
      },
    }),

    getAllStates: builder.query<
      LocationResponse<{
        states: Array<State>;
      }>,
      { country: string; page: number; limit: number }
    >({
      query: (params: { country: string; page: number; limit: number }) => ({
        url: `/location/states`,
        method: "GET",
        params,
      }),

      // Provide tags for cache management - tied to country
      providesTags: ["StatesList"],

      // Cache configuration
      extraOptions: {
        maxRetries: 2,
        keepUnusedDataFor: 1800, // Keep in cache for 30 minutes
        refetchOnMountOrArgChange: false,
        refetchOnFocus: false,
        refetchOnReconnect: false,
      },
    }),

    getAllCities: builder.query<
      LocationResponse<{
        cities: Array<city>;
      }>,
      {
        state: string;
        country: string;
        page: number;
        limit: number;
      }
    >({
      query: (params: {
        state: string;
        country: string;
        page: number;
        limit: number;
      }) => ({
        url: `/location/cities`,
        method: "GET",
        params,
      }),

      // Provide tags for cache management - tied to state and country
      providesTags: ["CitiesList"],

      // Cache configuration
      extraOptions: {
        maxRetries: 2,
        keepUnusedDataFor: 1800, // Keep in cache for 30 minutes
        refetchOnMountOrArgChange: false,
        refetchOnFocus: false,
        refetchOnReconnect: false,
      },
    }),
  }),
});

export const {
  useGetAllCountriesQuery,
  useGetAllStatesQuery,
  useGetAllCitiesQuery,
} = locationApi;
